import {
  getClientDevice,
  getHubPageName,
  getUtmParamsFromUrl,
  initMixpanel,
  mapEnvironmentString,
  registerMixpanelSuperProperties,
} from '@monorepo/client-common';
import { CoralogixRum, UrlType } from '@coralogix/browser';
import {
  browserTracingIntegration,
  captureConsoleIntegration,
  httpContextIntegration,
  init,
  replayIntegration,
} from '@sentry/react';
import gtmService from '../services/analytics';
import { Environment } from '../services/core/env-service';

/**
 * Initializes LeadDyno with configurable options
 */

export class LeadDynoInitializer {
  constructor(
    private settings: {
      domainName: string | undefined;
      leaddynoPublicKey: string;
    },
  ) {}

  initialize() {
    try {
      if (typeof window.LeadDyno === 'undefined') {
        return;
      }
      const { domainName, leaddynoPublicKey } = this.settings;

      if (domainName) {
        window.LeadDyno.domain = domainName;
      }
      window.LeadDyno.key = leaddynoPublicKey;
      window.LeadDyno.recordVisit();
    } catch (error) {
      console.error('LeadDyno initialization failed:', error);
    }
  }
}

/**
 * Initializes Sentry with configurable options
 */
export class SentryInitializer {
  private static readonly replaySessionSampleRate = 0.1;
  private static readonly replayErrorSampleRate = 1.0;

  static initialize({
    sentryDsn,
    isProduction,
    appEnv,
  }: {
    isProduction?: boolean;
    sentryDsn?: string;
    appEnv?: string;
  }) {
    if (sentryDsn) {
      // Generate release identifier for Session Replay (required for Session Replay to work)
      // Using environment name as release since we don't use versioning
      const release = `${appEnv || 'development'}-local`;

      // Build integrations array - conditionally include Session Replay only in production
      const integrations = [
        browserTracingIntegration(),
        httpContextIntegration(),
        // Capture console logs (error, warn, log, info, debug)
        captureConsoleIntegration({
          levels: ['error', 'warn'],
        }),
      ];

      // Only enable Session Replay in production to avoid rate limiting in development
      if (isProduction) {
        integrations.push(
          replayIntegration({
            networkDetailAllowUrls: [
              'https://api.paysafe.com/checkout/api/v1/log',
            ],
            networkRequestHeaders: ['X-Custom-Header'],
            networkResponseHeaders: ['X-Custom-Header'],
            maskAllText: false,
            blockAllMedia: false,
          }),
        );
      }

      init({
        dsn: sentryDsn,
        environment: appEnv || 'development', // Tag with correct environment
        release: release, // Required for Session Replay (uses environment name since we don't use versioning)
        // Enable debug mode for detailed logs (Sentry internal logs)
        debug: !isProduction, // Only in dev - users won't see Sentry internals in prod
        integrations,
        beforeSend(event, hint) {
          // Filter by URL/Route - exclude specific pages
          const url = event.request?.url || window.location.href;
          const excludedRoutes: string[] = ['/overview', '/trade-talk'];
          // Check if current URL matches any excluded route
          if (excludedRoutes.some((route) => url.includes(route))) {
            return null; // Don't send errors from these pages
          }

          // Filter by error type/message
          const error = hint?.originalException;
          if (
            (typeof error === 'string' && error.includes('Timeout')) ||
            (error instanceof DOMException && error.name === 'AbortError') ||
            (error instanceof Error &&
              error.message.includes('Token refresh failed'))
          ) {
            return null; // 👈 don't send to Sentry
          }

          return event;
        },
        // Session Replay sample rates - only used in production (replay disabled in dev)
        replaysSessionSampleRate: isProduction
          ? SentryInitializer.replaySessionSampleRate
          : 0, // Disabled in dev to avoid rate limiting
        replaysOnErrorSampleRate: isProduction
          ? SentryInitializer.replayErrorSampleRate
          : 0, // Disabled in dev to avoid rate limiting
        // Enable performance monitoring with lower rates in dev
        tracesSampleRate: isProduction ? 0.1 : 0.01,
        // Enable profiling (requires tracesSampleRate > 0)
        profilesSampleRate: isProduction ? 0.1 : 0.01,
        // Enable automatic instrumentation
        enableTracing: true,
      });
      // These logs are visible in browser console (users can see if they open DevTools)
      if (!isProduction) {
        console.log(`[Sentry] Initialized for environment: ${appEnv}`);
        console.log(
          `[Sentry] Tracing enabled with sample rate: ${isProduction ? 0.1 : 0.01}`,
        );
        console.log(`[Sentry] Console logs capture enabled`);
      }
    } else {
      console.warn('[Sentry] Skipped initialization (no DSN provided).');
    }
  }
}

/**
 * Initializes all third-party services
 */
export const initializeThirdPartyServices = (config: Environment) => {
  // Sentry initialization
  SentryInitializer.initialize({
    sentryDsn: config.sentryDsn,
    isProduction: config.isProduction,
    appEnv: config.appEnv,
  });

  // GTM initialization
  const gtmId = config.gtmId;
  if (gtmId) {
    gtmService.initialize(gtmId);
  } else {
    console.warn('[GTM] Skipped initialization (no GTM ID).');
  }

  const mixpanelToken = config.mixPanelToken;
  const reactEnv = config.appEnv;

  // Capture UTM params from URL early — before Mixpanel init
  // These are persisted to localStorage for the HTTP interceptor
  const utmParams = getUtmParamsFromUrl();

  if (mixpanelToken) {
    const mixpanelEnv = mapEnvironmentString(reactEnv);

    initMixpanel({
      token: mixpanelToken,
      env: mixpanelEnv,
      apiHost: config.mixPanelApiHost,
    });

    const initialLanguage = localStorage.getItem('i18nextLng') || 'en';

    // Register super properties — sent with every Mixpanel event
    // UTM params are only included when present in the URL to avoid clearing previous values
    registerMixpanelSuperProperties({
      brand: config.application,
      device: getClientDevice(),
      hub_language: initialLanguage.toLowerCase(),
      ...utmParams,
    });
  } else {
    // Even without Mixpanel, persist UTMs to localStorage for backend tracking
    registerMixpanelSuperProperties(utmParams);
    console.warn('[Mixpanel] Skipped initialization (no Mixpanel token).');
  }

  // Coralogix RUM initialization (shutdown first to handle Vite HMR re-init)
  if (config.coralogixRumPublicKey) {
    CoralogixRum.shutdown();
    CoralogixRum.init({
      public_key: config.coralogixRumPublicKey,
      coralogixDomain: config.coralogixRumDomain,
      application: config.application,
      version: config.version,
      debug: !config.isProduction,
      instrumentations: config.coralogixRumInstrumentations,
      labelProviders: [
        {
          urlType: UrlType.PAGE,
          providerFunc: (url: string) => {
            try {
              const pathname = new URL(url).pathname;
              return { page_name: getHubPageName(pathname) };
            } catch {
              return { page_name: 'unknown' };
            }
          },
        },
      ],
    });
  } else {
    console.warn(
      '[Coralogix RUM] Skipped initialization (no public key provided).',
    );
  }

  // LeadDyno initialization
  const leadDynoInitializer = new LeadDynoInitializer({
    domainName: config.domainName,
    leaddynoPublicKey: config.leaddynoPublicKey,
  });

  leadDynoInitializer.initialize();
};
